import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
// Fair-exposure shuffle: listing pages rotate their order on every visit as the magazine grows.
export const shuffled = (arr) => [...(arr || [])].sort(() => Math.random() - 0.5);

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("glossy_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const pv = previewIssue();
  if (pv && (config.method || "get").toLowerCase() === "get") {
    config.params = { ...(config.params || {}), preview_issue: pv };
  }
  return config;
});

export default client;

// Edition dress rehearsal — when the editor turns this on (Studio → Editions → Preview next edition),
// every public read carries preview_issue so the whole site renders as that edition. Editor-only, server-enforced.
export const PREVIEW_KEY = "sme_preview_issue";
export function previewIssue() {
  try { return localStorage.getItem(PREVIEW_KEY); } catch { return null; }
}
export function exitPreview() {
  try { localStorage.removeItem(PREVIEW_KEY); } catch { /* noop */ }
  window.location.assign("/");
}

// Branded fallback so a cover/portrait is never an empty src.
export const DEFAULT_COVER =
  "https://images.unsplash.com/photo-1697656359797-fb8d0a803a48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1300";

// Resolve an image: AI-generated storage path -> served file; else fallback Unsplash url.
export function imgUrl(obj) {
  if (!obj) return DEFAULT_COVER;
  if (obj.cover_image) return `${API}/files/${obj.cover_image}`;
  if (obj.fallback_image) return obj.fallback_image;
  return DEFAULT_COVER;
}

// Resolve a member's company logo: uploaded storage path -> served file; else fallback.
export function logoUrl(m) {
  if (!m) return null;
  if (m.logo) return `${API}/files/${m.logo}`;
  if (m.fallback_logo) return m.fallback_logo;
  return null;
}

// Resolve any asset that may be a storage path OR an absolute URL (notice logos/posters).
export function assetUrl(val) {
  if (!val) return null;
  if (val.startsWith("http")) return val;
  return `${API}/files/${val}`;
}

// Shared advert pool fetch with in-memory cache. Placements are FIXED per edition —
// one member holds one named space for the full fortnight, no rotation, no shuffling.
let _adCache = null;
export async function fetchAdverts() {
  if (_adCache) return _adCache;
  try {
    const r = await client.get("/adverts");
    _adCache = r.data || [];
  } catch {
    _adCache = [];
  }
  return _adCache;
}

// Advert image: dedicated campaign image -> served file; else fall back to the member cover.
export function advertImgUrl(ad) {
  if (ad?.advert_image) return `${API}/files/${ad.advert_image}`;
  return imgUrl(ad);
}

// One member holds one space for a full edition — deterministic, no refresh shuffling.
export function adForSpace(list, space, excludeMemberId = null) {
  return (list || []).find((a) => a.slot === space && a.member_id !== excludeMemberId && a.advert) || null;
}

// Resolve where an advert clicks through to, based on the member's chosen link_type.
// Returns { href, external } — external opens in a new tab.
export function resolveAdLink(ad) {
  if (!ad) return { href: "#", external: false };
  const norm = (u) => (u ? (/^https?:\/\//i.test(u) ? u : `https://${u}`) : null);
  const profile = `/member/${ad.member_id}`;
  switch (ad.link_type) {
    case "website":
      return ad.website ? { href: norm(ad.website), external: true } : { href: profile, external: false };
    case "profile":
      return { href: profile, external: false };
    case "offer":
      return { href: `${profile}#offer`, external: false };
    case "contact":
      return ad.email ? { href: `mailto:${ad.email}`, external: true } : { href: `${profile}#contact`, external: false };
    case "custom":
      return ad.link_url ? { href: norm(ad.link_url), external: true } : { href: profile, external: false };
    default: // auto — website if available, else profile
      return ad.website ? { href: norm(ad.website), external: true } : { href: profile, external: false };
  }
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Cover shoots run as background jobs (production proxy kills requests over ~100s).
// Drop-in replacement for the old sync call: returns { data } with the same shape.
export async function shootCover(memberId, payload) {
  const start = await client.post(`/admin/members/${memberId}/generate-cover`, payload);
  const jobId = start.data.job_id;
  for (let i = 0; i < 90; i++) {
    await new Promise((res) => setTimeout(res, 4000));
    try {
      const s = await client.get(`/admin/members/${memberId}/cover-job/${jobId}`);
      if (s.data.status === "ready") return { data: s.data };
      if (s.data.status === "failed") { const err = new Error(s.data.error || "Generation failed"); err.response = { data: { detail: s.data.error || "Generation failed — try again." } }; throw err; }
    } catch (e) { if (e.response?.data?.detail) throw e; }
  }
  const err = new Error("Timed out");
  err.response = { data: { detail: "The studio is taking too long — try again." } };
  throw err;
}

// Generic version of shootCover for ALL long generations: POST returns {job_id},
// poll /admin/jobs/{id}; resolves with { data: result } in the endpoint's old shape.
export async function runJob(path, payload) {
  const start = await client.post(path, payload ?? {});
  if (!start.data?.job_id) return start;
  for (let i = 0; i < 120; i++) {
    await new Promise((res) => setTimeout(res, 4000));
    try {
      const s = await client.get(`/admin/jobs/${start.data.job_id}`);
      if (s.data.status === "ready") return { data: s.data.result };
      if (s.data.status === "failed") { const err = new Error(s.data.error || "Failed"); err.response = { data: { detail: s.data.error || "Generation failed — try again." } }; throw err; }
    } catch (e) { if (e.response?.data?.detail) throw e; }
  }
  const err = new Error("Timed out");
  err.response = { data: { detail: "The studio is taking too long — try again." } };
  throw err;
}
